import { RefObject, useCallback, useMemo } from 'react'

import { Header, flexRender } from '@tanstack/react-table'
import { Flex } from 'antd'
import isNil from 'lodash.isnil'
import styled, { css } from 'styled-components'
import { twMerge } from 'tailwind-merge'

import { getFontSize, TextSize } from 'src/components/Typography/Text'

import { Size } from '../../types'
import QuickTip from '../QuickTip/QuickTip'
import { T } from '../Text/Text'
import Tooltip from '../Tooltip/Tooltip'

import { useTableContext } from './context'
import { ColumnHeaderActionsConfig } from './headerActions'
import TableHeaderActionsPopover from './TableHeaderActionsPopover'
import TableSortIndicator from './TableSortIndicator'
import { TableColumnId } from './types'
import { TableCellStickyStyles, TableCellStickyStylesProps } from './util'

const MAX_HEADER_CHARS = 40

type TableHeaderCellStyleProps = {
  $horizontalPaddingOverride?: React.CSSProperties['paddingLeft']
  $isSortable?: boolean
  $isStickyHeader?: boolean
  $size?: Size
  $textAlign?: React.CSSProperties['textAlign']
  $width: React.CSSProperties['width']
  $minWidth?: React.CSSProperties['minWidth']
} & TableCellStickyStylesProps
export const TableHeaderCellStyles = css<TableHeaderCellStyleProps>`
  width: ${({ $width }) => $width};
  ${({ $minWidth }) => $minWidth && `min-width: ${$minWidth};`}
  text-align: ${({ $textAlign }) => $textAlign ?? 'left'};
  padding: ${({ $horizontalPaddingOverride }) => {
    const verticalPadding = '8px'
    const horizontalPadding = isNil($horizontalPaddingOverride) ? '8px' : $horizontalPaddingOverride
    return `${verticalPadding} ${horizontalPadding}`
  }};
  &:first-child {
    padding-left: ${({ $horizontalPaddingOverride }) =>
      !isNil($horizontalPaddingOverride) ? $horizontalPaddingOverride : '12px'};
  }
  &:last-child {
    padding-right: ${({ $horizontalPaddingOverride }) =>
      !isNil($horizontalPaddingOverride) ? $horizontalPaddingOverride : '12px'};
  }
  cursor: ${({ $isSortable: $isSortingEnabled }) => ($isSortingEnabled ? 'pointer' : 'default')};
  border-bottom: solid 1px ${({ theme }) => theme.colorSplit};

  font-family: ${({ theme }) => theme.fontFamily};
  font-size: ${getFontSize(TextSize.Base)}px;
  color: ${({ theme }) => theme.colorText};
  font-weight: 400;

  ${TableCellStickyStyles};

  /* Sticky-header mode: pin to top, z-index 2 (plain) or 3 (corner). */
  ${({ $isStickyHeader, $stickiness }) =>
    $isStickyHeader
      ? `
        position: sticky;
        top: 0;
        z-index: ${$stickiness === 'none' ? 2 : 3};
      `
      : ''};
`

export const StyledTD = styled.td<TableHeaderCellStyleProps>`
  ${TableHeaderCellStyles}
`

const StyledTH = styled.th<TableHeaderCellStyleProps>`
  ${TableHeaderCellStyles}
`

type Props<TData> = {
  header: Header<TData, unknown>
  /**
   * When non-null, renders an always-visible chevron + popover next to
   * the header label. Threaded down from the parent `Table` prop and
   * populated by the report-preview surface only.
   */
  headerActions: ColumnHeaderActionsConfig | null
  headersByColumnKeyRef: RefObject<Map<string, HTMLTableCellElement>>
  isScrolledToLeft: boolean
  isScrolledToRight: boolean
  isSortingEnabled: boolean
  isStickyHeader: boolean
  left: number
  right: number
  stickiness: 'left' | 'right' | 'none'
}
const TableHeaderCell = <TData,>({
  header,
  headerActions,
  headersByColumnKeyRef,
  isScrolledToLeft,
  isScrolledToRight,
  isSortingEnabled,
  isStickyHeader,
  left,
  right,
  stickiness,
}: Props<TData>) => {
  // When a per-column popover is provided, it owns the sort UX entirely:
  // the header label is no longer click-to-sort, the cell loses its
  // pointer cursor and "Sort ascending" tooltip, and the unsorted
  // hover-preview indicator is suppressed (the popover replaces that
  // affordance). TanStack's sort state still tracks normally so the
  // indicator can render when a column IS sorted via the popover.
  const isSortViaCellClickEnabled = isSortingEnabled && header.column.getCanSort() && !headerActions
  const { size } = useTableContext()
  const children = flexRender(header.column.columnDef.header, header.getContext())
  const width = header.column.columnDef.meta?.width
  const minWidth = header.column.columnDef.meta?.minWidth

  const autoSortDir = header.column.getAutoSortDir()
  const isSorted = header.column.getIsSorted()
  const tooltip = useMemo(() => {
    if (!isSortViaCellClickEnabled) {
      return ''
    }
    if (autoSortDir === 'desc') {
      return isSorted === 'asc'
        ? 'Remove sorting'
        : isSorted === 'desc'
          ? 'Sort ascending'
          : 'Sort descending'
    }
    return isSorted === 'asc'
      ? 'Sort descending'
      : isSorted === 'desc'
        ? 'Remove sorting'
        : 'Sort ascending'
  }, [autoSortDir, isSortViaCellClickEnabled, isSorted])

  // Empty table header cells are accessibility errors - the fix is to use a td instead in those scenarios
  const shouldRenderHeaderAsTd = header.column.columnDef.meta?.shouldRenderHeaderAsTd || !children
  const Cell = useMemo(
    () => (shouldRenderHeaderAsTd ? StyledTD : StyledTH),
    [shouldRenderHeaderAsTd]
  )

  const ref = useCallback(
    (node: HTMLTableCellElement | null) => {
      if (node && headersByColumnKeyRef.current) {
        headersByColumnKeyRef.current.set(header.column.id, node)
      }
    },
    [header.column.id, headersByColumnKeyRef]
  )

  return (
    <Tooltip content={tooltip}>
      <Cell
        ref={ref}
        colSpan={header.colSpan}
        $horizontalPaddingOverride={
          header.column.columnDef.meta?.dangerouslyOverrideHorizontalPadding
        }
        $isScrolledToLeft={isScrolledToLeft}
        $isScrolledToRight={isScrolledToRight}
        $isSortable={isSortViaCellClickEnabled}
        $isStickyHeader={isStickyHeader}
        $left={left}
        $minWidth={minWidth}
        $right={right}
        $size={size}
        $stickiness={stickiness}
        $textAlign={header.column.columnDef.meta?.textAlign}
        $width={width ? width : 'auto'}
        onClick={isSortViaCellClickEnabled ? header.column.getToggleSortingHandler() : undefined}
        className={twMerge(
          'group bg-neutral-100',
          // Per-column override via `headerCellClassName` meta — twMerge
          // collapses conflicting `bg-*` declarations so a column can drop
          // or replace the default header strip (e.g. set `bg-transparent`
          // to clear it, or `bg-amber-50` to highlight a column).
          header.column.columnDef.meta?.headerCellClassName
        )}
      >
        <Flex
          gap="6px"
          align="center"
          justify={
            header.column.columnDef.meta?.textAlign === 'right'
              ? 'flex-end'
              : header.column.columnDef.meta?.textAlign === 'center'
                ? 'center'
                : 'flex-start'
          }
        >
          <Flex
            gap="6px"
            align="center"
            justify={
              header.column.columnDef.meta?.textAlign === 'right' ? 'flex-end' : 'flex-start'
            }
          >
            {typeof children === 'string' ? (
              children.length > MAX_HEADER_CHARS ? (
                <>
                  <T.Body className="whitespace-nowrap text-neutral-600">
                    {`${children.slice(0, MAX_HEADER_CHARS)}...`}
                  </T.Body>
                  <QuickTip content={children} />
                </>
              ) : (
                <T.Body className="whitespace-nowrap text-neutral-600">{children}</T.Body>
              )
            ) : (
              children
            )}
          </Flex>
          {isSortViaCellClickEnabled ? (
            <div className={header.column.getIsSorted() ? '' : 'invisible group-hover:visible'}>
              <TableSortIndicator autoSortDir={autoSortDir} column={header.column} />
            </div>
          ) : (
            // Popover-driven sort: only render the indicator when a sort
            // is actively applied. No reserved hover-preview slot — the
            // popover replaces that affordance, and reserving an
            // invisible slot was creating subpixel layout drift between
            // adjacent header cells.
            !!isSorted && (
              <div>
                <TableSortIndicator autoSortDir={autoSortDir} column={header.column} />
              </div>
            )
          )}
          {headerActions &&
            // Skip synthetic table columns (selection / reorder / expand)
            // — they have stable internal ids and no domain-level
            // generatedId for the popover to operate on.
            header.column.id !== TableColumnId.Expand &&
            header.column.id !== TableColumnId.Reorder &&
            header.column.id !== TableColumnId.Select && (
              <TableHeaderActionsPopover
                columnGeneratedId={header.column.id}
                headerActions={headerActions}
              />
            )}
        </Flex>
      </Cell>
    </Tooltip>
  )
}

export default TableHeaderCell
